import { type CSSProperties, type HTMLAttributes, type ReactNode, type Ref } from 'react';
import clsx from 'clsx';
import './Progress.css';
import type { ProgressTone } from './ProgressBar.js';

export interface CircularProgressProps extends Omit<HTMLAttributes<HTMLDivElement>, 'children'> {
  value: number;
  tone?: Exclude<ProgressTone, 'danger'>;
  /** Diameter in px. Default 64. */
  size?: number;
  /** Ring stroke thickness in px. Default ≈10% of size. */
  thickness?: number;
  /** Overlay text (e.g. "3/5" or "60%"). Not required. */
  label?: ReactNode;
  /** ARIA label (what this progress represents). */
  ariaLabel?: string;
  ref?: Ref<HTMLDivElement>;
}

/**
 * CircularProgress — a ring sliced to show a fraction. Used in
 * lesson players ("Exercise 3 of 8") or any compact progress view.
 */
export function CircularProgress({
  value,
  tone = 'primary',
  size = 64,
  thickness,
  label,
  ariaLabel,
  className,
  style,
  ref,
  ...rest
}: CircularProgressProps) {
  const pct = Math.max(0, Math.min(1, value));
  const strokeWidth = thickness ?? Math.max(4, Math.round(size * 0.12));
  const radius = (100 - strokeWidth) / 2; // viewBox math in % space
  const circumference = 2 * Math.PI * radius;
  const dashOffset = circumference * (1 - pct);

  const wrapperStyle: CSSProperties = {
    ...style,
    width: size,
    height: size,
    fontSize: Math.max(12, Math.round(size * 0.28)),
  };

  return (
    <div
      ref={ref}
      className={clsx('fluent-cprogress', className)}
      data-tone={tone}
      style={wrapperStyle}
      role="progressbar"
      aria-valuenow={Math.round(pct * 100)}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={ariaLabel}
      {...rest}
    >
      <svg viewBox="0 0 100 100" aria-hidden="true">
        <circle className="fluent-cprogress__track" cx="50" cy="50" r={radius} strokeWidth={strokeWidth} />
        <circle
          className="fluent-cprogress__fill"
          cx="50"
          cy="50"
          r={radius}
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={dashOffset}
        />
      </svg>
      {label != null && <span className="fluent-cprogress__label">{label}</span>}
    </div>
  );
}
